import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Lock, ArrowRight, Sparkles, Shield, RefreshCw, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import { GoogleLogin } from "@react-oauth/google";
import { Logo } from "@/components/Logo";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";

export default function Login() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState("credentials"); // "credentials" | "otp"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  // OTP state
  const [otpSessionId, setOtpSessionId] = useState("");
  const [otpEmailMasked, setOtpEmailMasked] = useState("");
  const [otpExpiresAt, setOtpExpiresAt] = useState(null);
  const [otpCode, setOtpCode] = useState("");
  const [otpDeliveryMsg, setOtpDeliveryMsg] = useState("");
  const [devOtp, setDevOtp] = useState(null); // surfaced only when SMTP fails / not configured
  const [resendCooldown, setResendCooldown] = useState(0);
  const [remaining, setRemaining] = useState(0);

  // resend cooldown tick
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = setInterval(() => setResendCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(id);
  }, [resendCooldown]);

  // remaining time before OTP expires
  useEffect(() => {
    if (!otpExpiresAt) return;
    const tick = () => {
      const ms = new Date(otpExpiresAt).getTime() - Date.now();
      setRemaining(Math.max(0, Math.floor(ms / 1000)));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [otpExpiresAt]);

  if (user) return <Navigate to="/dashboard" replace />;

  const applyOtpChallenge = (data) => {
    setOtpSessionId(data.otp_session_id);
    setOtpEmailMasked(data.email_masked || email);
    setOtpExpiresAt(data.expires_at);
    setOtpDeliveryMsg(data.delivery_message || "");
    setDevOtp(data.dev_otp || null);
    setOtpCode("");
    setResendCooldown(30);
    setStep("otp");
    if (data.dev_mode) {
      toast.warning("Mail delivery problem — see code below");
    } else {
      toast.success("Code sent — check your inbox");
    }
  };

  const finalizeJwtLogin = (data) => {
    localStorage.setItem("munners_token", data.token);
    setUser(data.user);
    toast.success("Welcome back!");
    navigate("/dashboard");
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/auth/login", { email: email.trim().toLowerCase(), password });
      if (r.data.otp_required) applyOtpChallenge(r.data);
      else finalizeJwtLogin(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleSuccess = async (resp) => {
    try {
      const r = await api.post("/auth/google/id-token", { credential: resp.credential });
      if (r.data.otp_required) applyOtpChallenge(r.data);
      else finalizeJwtLogin(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Google sign-in failed");
    }
  };
  const handleGoogleError = () => toast.error("Google sign-in was cancelled or failed.");

  const verifyOtp = async (e) => {
    e?.preventDefault?.();
    if (!otpCode || otpCode.length !== 6) {
      toast.error("Enter the 6-digit code");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/auth/verify-otp", { otp_session_id: otpSessionId, otp: otpCode });
      finalizeJwtLogin(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verification failed");
    } finally {
      setBusy(false);
    }
  };

  const resendOtp = async () => {
    if (resendCooldown > 0 || !otpSessionId) return;
    setBusy(true);
    try {
      const r = await api.post("/auth/resend-otp", { otp_session_id: otpSessionId });
      applyOtpChallenge(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not resend");
    } finally {
      setBusy(false);
    }
  };

  const backToCredentials = () => {
    setStep("credentials");
    setOtpSessionId("");
    setOtpCode("");
    setOtpExpiresAt(null);
    setDevOtp(null);
  };

  return (
    <div className="min-h-screen w-full grid lg:grid-cols-2 relative overflow-hidden bg-white">
      {/* Floating gradient blobs */}
      <div className="blob animate-float bg-orange-300" style={{ width: 500, height: 500, top: -100, left: -100 }} />
      <div className="blob animate-float bg-amber-300" style={{ width: 400, height: 400, bottom: -150, right: -100, animationDelay: "3s" }} />
      <div className="blob animate-float bg-pink-200" style={{ width: 300, height: 300, top: "40%", left: "30%", animationDelay: "6s", opacity: 0.3 }} />

      {/* Left side - brand */}
      <div className="hidden lg:flex relative flex-col justify-between p-12 z-10">
        <div><Logo size="lg" /></div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-100 text-orange-700 text-xs font-semibold mb-6">
            <Sparkles className="w-3.5 h-3.5" /> The MUN Operations Platform
          </div>
          <h1 className="font-display text-5xl xl:text-6xl font-black text-slate-900 leading-tight tracking-tighter">
            Run brilliant
            <span className="block brand-gradient-text">Model UN events.</span>
          </h1>
          <p className="mt-5 text-slate-600 text-lg max-w-md leading-relaxed">
            One platform for project managers, social teams, principals, and executive boards —
            from proposal to allotment, budget to QR check-in.
          </p>
          <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
            {[
              { k: "events", v: "100+" },
              { k: "delegates", v: "5,000" },
              { k: "workflows", v: "17 steps" },
            ].map((s) => (
              <div key={s.k} className="dashboard-card p-4">
                <div className="text-2xl font-display font-black brand-gradient-text">{s.v}</div>
                <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">{s.k}</div>
              </div>
            ))}
          </div>
        </motion.div>
        <div className="text-xs text-slate-400">© 2026 MUNner&apos;s · Existing members only · No public signup</div>
      </div>

      {/* Right - login form */}
      <div className="relative z-10 flex items-center justify-center p-6 lg:p-12">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}
          className="w-full max-w-md dashboard-card p-8" data-testid="login-card">
          <div className="lg:hidden mb-6 flex justify-center"><Logo size="lg" /></div>

          <AnimatePresence mode="wait">
            {step === "credentials" && (
              <motion.div key="creds" initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }}>
                <h2 className="font-display text-3xl font-black text-slate-900">Welcome back</h2>
                <p className="text-slate-500 text-sm mt-1">Sign in to your MUNner&apos;s workspace.</p>

                <div className="mt-6 flex justify-center" data-testid="google-login-btn">
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={handleGoogleError}
                    theme="outline" size="large" shape="pill" text="continue_with" width="320"
                  />
                </div>

                <div className="my-6 flex items-center gap-3">
                  <div className="flex-1 h-px bg-slate-200" />
                  <div className="text-xs text-slate-400 uppercase tracking-wider">or with email</div>
                  <div className="flex-1 h-px bg-slate-200" />
                </div>

                <form onSubmit={handleEmailLogin} className="space-y-4">
                  <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus-within:border-orange-400 focus-within:ring-4 focus-within:ring-orange-100 focus-within:bg-white transition-all">
                    <Mail className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    <input data-testid="login-email-input" required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com" className="flex-1 bg-transparent outline-none text-slate-900 placeholder:text-slate-400" />
                  </div>
                  <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus-within:border-orange-400 focus-within:ring-4 focus-within:ring-orange-100 focus-within:bg-white transition-all">
                    <Lock className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    <input data-testid="login-password-input" required type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                      placeholder="Password" className="flex-1 bg-transparent outline-none text-slate-900 placeholder:text-slate-400" />
                  </div>
                  <button data-testid="login-submit-btn" type="submit" disabled={busy} className="btn-primary w-full py-3">
                    {busy ? "Signing in…" : (<>Sign in <ArrowRight className="w-4 h-4" /></>)}
                  </button>
                </form>

                <p className="text-xs text-slate-400 mt-6 text-center">
                  Accounts are created by Admin. No public signup.
                </p>
              </motion.div>
            )}

            {step === "otp" && (
              <motion.div key="otp" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 16 }}>
                <button onClick={backToCredentials} className="text-xs text-slate-500 flex items-center gap-1 mb-4 hover:text-slate-900" data-testid="otp-back-btn">
                  <ChevronLeft className="w-3 h-3" /> Back
                </button>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-2xl bg-orange-100 text-orange-600 flex items-center justify-center">
                    <Shield className="w-5 h-5" />
                  </div>
                  <h2 className="font-display text-2xl font-black text-slate-900">Verify your sign-in</h2>
                </div>
                <p className="text-slate-500 text-sm mt-1">
                  We sent a 6-digit code to <b className="text-slate-700">{otpEmailMasked}</b>.
                  {remaining > 0 && <span> Expires in <b>{Math.floor(remaining/60)}:{String(remaining%60).padStart(2,"0")}</b>.</span>}
                </p>

                <form onSubmit={verifyOtp} className="space-y-4 mt-6">
                  <OtpInput value={otpCode} onChange={setOtpCode} onComplete={(v) => { setOtpCode(v); }} />

                  <button data-testid="otp-submit-btn" type="submit" disabled={busy || otpCode.length !== 6} className="btn-primary w-full py-3">
                    {busy ? "Verifying…" : (<>Verify & sign in <ArrowRight className="w-4 h-4" /></>)}
                  </button>

                  <div className="flex items-center justify-between text-xs">
                    <button type="button" onClick={resendOtp} disabled={resendCooldown > 0 || busy}
                      className="text-orange-600 hover:text-orange-800 flex items-center gap-1 disabled:text-slate-400 disabled:cursor-not-allowed"
                      data-testid="otp-resend-btn">
                      <RefreshCw className="w-3 h-3" />
                      {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend code"}
                    </button>
                    <span className="text-slate-400">Didn&apos;t get it? Check spam.</span>
                  </div>
                </form>

                {devOtp && (
                  <div className="mt-5 p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-xs">
                    <div className="font-bold mb-1">⚠ Email delivery issue</div>
                    <div className="text-[11px] leading-snug mb-2">{otpDeliveryMsg || "SMTP isn't configured / failed. Use the code below to continue and ask your admin to fix SMTP."}</div>
                    <div className="text-center font-mono text-2xl tracking-[0.4em] font-black text-amber-700 select-all" data-testid="otp-dev-code">{devOtp}</div>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
}

/** 6-box OTP input */
function OtpInput({ value, onChange, onComplete }) {
  const refs = useRef([...Array(6)].map(() => null));
  const digits = (value || "").padEnd(6, " ").slice(0, 6).split("");

  const setDigit = (i, d) => {
    const cur = (value || "").padEnd(6, " ").split("");
    cur[i] = d;
    const next = cur.join("").replace(/\s/g, "").slice(0, 6);
    onChange(next);
    if (next.length === 6) onComplete?.(next);
  };

  return (
    <div className="flex justify-between gap-2" data-testid="otp-input-group">
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => (refs.current[i] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          autoFocus={i === 0}
          value={d.trim()}
          onChange={(e) => {
            const v = e.target.value.replace(/[^0-9]/g, "").slice(0, 1);
            setDigit(i, v);
            if (v && i < 5) refs.current[i + 1]?.focus();
          }}
          onKeyDown={(e) => {
            if (e.key === "Backspace" && !d.trim() && i > 0) refs.current[i - 1]?.focus();
            if (e.key === "ArrowLeft" && i > 0) refs.current[i - 1]?.focus();
            if (e.key === "ArrowRight" && i < 5) refs.current[i + 1]?.focus();
          }}
          onPaste={(e) => {
            const text = (e.clipboardData.getData("text") || "").replace(/[^0-9]/g, "").slice(0, 6);
            if (text.length) {
              e.preventDefault();
              onChange(text);
              if (text.length === 6) onComplete?.(text);
              refs.current[Math.min(text.length, 5)]?.focus();
            }
          }}
          className="w-12 h-14 sm:w-14 sm:h-16 text-center text-2xl font-display font-black bg-slate-50 border-2 border-slate-200 rounded-xl outline-none focus:border-orange-400 focus:ring-4 focus:ring-orange-100 focus:bg-white transition-all"
          data-testid={`otp-input-${i}`}
        />
      ))}
    </div>
  );
}
